import {request} from '@/api';
import {FeedFile} from '@/api/models/FeedFiles.ts';
import {Create, Entity, Normalize, Stub, Update} from '@/api/models/index.ts';

export interface Feed extends Entity {
	name: string,
	file: Stub<FeedFile, 'name' | 'path'>,
	fields: FeedField[],
}

export interface FeedField extends Entity {
	feed: Stub<Feed>,
	feedKey: string,
	schemaKey: Field,
	mapper: Mapper | null,
	fallbackValue: string | number | null,
}

interface BaseMapper {
	kind: MapperKind,
}

export interface ChainMapper extends BaseMapper {
	kind: MapperKind.Chain,
	mappers: Mapper[],
}

export interface LookupMapper extends BaseMapper {
	kind: MapperKind.Lookup,
	valueMap: { [key: string]: string },
}

export interface SplitMapper extends BaseMapper {
	kind: MapperKind.Split,
	separator: string,
}

export interface TakeOneMapper extends BaseMapper {
	kind: MapperKind.TakeOne,
	index: number,
}

export type Mapper = ChainMapper | LookupMapper | SplitMapper | TakeOneMapper;

export enum MapperKind {
	Chain = 'chain',
	Lookup = 'lookup',
	Split = 'split',
	TakeOne = 'take-one',
}

export const MAPPER_NAMES: { [key in MapperKind]: string } = {
	[MapperKind.Chain]: 'Chain',
	[MapperKind.Lookup]: 'Lookup',
	[MapperKind.Split]: 'Split',
	[MapperKind.TakeOne]: 'Take One',
};

export const MAPPER_VALUES = Object.values(MapperKind).sort((a, b) => {
	const aVal = MAPPER_NAMES[a];
	const bVal = MAPPER_NAMES[b];

	return aVal.localeCompare(bVal);
});

export const MAPPER_DESCRIPTIONS: { [key in MapperKind]: string } = {
	[MapperKind.Chain]: 'Allows you to run multiple mappers in sequence',
	[MapperKind.Lookup]: 'Maps from one value to another, e.g. "N" to "new"',
	[MapperKind.Split]: 'Splits a string into multiple values on a separator; useful with Take One',
	[MapperKind.TakeOne]: 'Extracts a single element from an array of values',
};

export enum Field {
	Brand = 'brand',
	Condition = 'condition',
	Mileage = 'mileage',
	Model = 'model',
	Price = 'price',
	Vin = 'vin',
	Year = 'year',
	BodyType = 'bodyType',
	DriveWheelConfiguration = 'driveWheelConfiguration',
	Image = 'image',
	Name = 'name',
	DoorCount = 'doorCount',
	Url = 'url',
	ExteriorColor = 'exteriorColor',
	InteriorColor = 'interiorColor',
	SeatCount = 'seatCount',
	Transmission = 'transmission',
	Configuration = 'configuration',
}

export const FIELD_NAMES: { [key in Field]: string } = {
	[Field.BodyType]: 'Body Type',
	[Field.Condition]: 'Condition',
	[Field.Configuration]: 'Configuration',
	[Field.DoorCount]: 'Door Count',
	[Field.DriveWheelConfiguration]: 'Wheel Configuration',
	[Field.ExteriorColor]: 'Exterior Color',
	[Field.InteriorColor]: 'Interior Color',
	[Field.Image]: 'Image',
	[Field.Name]: 'Name',
	[Field.Brand]: 'Make',
	[Field.Model]: 'Model',
	[Field.Mileage]: 'Mileage',
	[Field.Price]: 'Price (USD)',
	[Field.SeatCount]: 'Seat Count',
	[Field.Transmission]: 'Transmission',
	[Field.Url]: 'URL',
	[Field.Vin]: 'VIN',
	[Field.Year]: 'Year',
};

export const FIELD_VALUES = Object.values(Field).sort((a, b) => {
	const aVal = FIELD_NAMES[a];
	const bVal = FIELD_NAMES[b];

	return aVal.localeCompare(bVal);
});

type FeedCreate = Omit<Create<Feed>, 'fields'> & {
	fields: Array<Omit<Create<FeedField>, 'feed'>>,
};

type FeedUpdate = Omit<Update<Feed>, 'fields'> & {
	fields: Array<Omit<Update<FeedField>, 'feed'>>,
};

type FeedClone = Normalize<Pick<Feed, 'name' | 'file'>>;

export namespace Feeds {
	export function list(): Promise<Feed[]> {
		return request('/feeds');
	}

	export function read(id: number): Promise<Feed> {
		return request(`/feeds/${id}`);
	}

	export function create(data: FeedCreate): Promise<Feed> {
		return request('/feeds', {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	export function update(id: number, data: FeedUpdate): Promise<Feed> {
		return request(`/feeds/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(data),
		});
	}

	export function remove(id: number): Promise<void> {
		return request(`/feeds/${id}`, {
			method: 'DELETE',
		});
	}

	export function clone(sourceId: number, data: FeedClone): Promise<Feed> {
		return request(`/feeds/${sourceId}/clone`, {
			method: 'POST',
			body: JSON.stringify(data),
		})
	}

	export function ingest(id: number): Promise<void> {
		return request(`/feeds/${id}/ingest`, {
			method: 'POST',
		});
	}
}
